import axios from 'axios';

// API base URL - use environment variable or localhost for development
const getApiBaseUrl = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Development mode
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on auth errors if we're on login page
    const isLoginPage = window.location.pathname === '/admin/login' || window.location.pathname === '/login';
    
    if (error.response?.status === 401 && !isLoginPage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Public API calls
export const getItems = (params = {}) => api.get('/api/items', { params });
export const getItem = (id) => api.get(`/api/items/${id}`);
export const getCategories = () => api.get('/api/categories');
export const submitContact = (data) => api.post('/api/contacts', data);

// Auth API calls
export const login = (credentials) => api.post('/api/auth/login', credentials);
export const logout = () => api.post('/api/auth/logout');

// Admin API calls
export const createItem = (formData) => 
  api.post('/api/admin/items', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const updateItem = (id, data) => api.put(`/api/admin/items/${id}`, data);
export const deleteItem = (id) => api.delete(`/api/admin/items/${id}`);
export const restoreItem = (id) => api.patch(`/api/items/${id}/restore`);

export const createCategory = (data) => api.post('/api/admin/categories', data);
export const updateCategory = (id, data) => api.put(`/api/admin/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/api/admin/categories/${id}`);

export const getInventory = () => api.get('/api/admin/inventory');
export const getContacts = () => api.get('/api/admin/contacts');

// Billing API calls
export const getInvoices = (params = {}) => api.get('/api/admin/billing/invoices', { params });
export const getInvoice = (id) => api.get(`/api/admin/billing/invoices/${id}`);
export const createInvoice = (data) => api.post('/api/admin/billing/invoices', data);
export const updateInvoice = (id, params) => api.put(`/api/admin/billing/invoices/${id}`, null, { params });

export const getCustomers = (params = {}) => api.get('/api/admin/billing/customers', { params });
export const getCustomer = (id) => api.get(`/api/admin/billing/customers/${id}`);

export const getPayments = (params = {}) => api.get('/api/admin/billing/payments', { params });
export const createPayment = (data) => api.post('/api/admin/billing/payments', data);

export const getDashboardStats = () => api.get('/api/admin/billing/dashboard');
export const getDailySales = (date) => api.get('/api/admin/sales/daily', { params: { date } });
export const getMonthlySales = (year, month) => api.get('/api/admin/sales/monthly', { params: { year, month } });

// Coupon API calls (public)
export const getActiveCoupons = () => api.get('/api/coupons/active');
export const validateCoupon = (code, cartTotal) => 
  api.get(`/api/coupons/validate/${code}`, { params: { cart_total: cartTotal } });

// Coupon Admin API calls
export const getCoupons = (params = {}) => api.get('/api/admin/coupons', { params });
export const createCoupon = (params) => api.post('/api/admin/coupons', null, { params });
export const updateCoupon = (id, params) => api.put(`/api/admin/coupons/${id}`, null, { params });
export const deleteCoupon = (id) => api.delete(`/api/admin/coupons/${id}`);

// Reviews API calls (public)
export const getItemReviews = (itemId, params = {}) => api.get(`/api/reviews/item/${itemId}`, { params });
export const submitReview = (data) => api.post('/api/reviews', data);
export const markReviewHelpful = (reviewId) => api.post(`/api/reviews/${reviewId}/helpful`);

// Reviews Admin API calls
export const getAdminReviews = (params = {}) => api.get('/api/admin/reviews', { params });
export const approveReview = (id, isVerified = false) => 
  api.put(`/api/admin/reviews/${id}/approve`, null, { params: { is_verified: isVerified } });
export const rejectReview = (id) => api.put(`/api/admin/reviews/${id}/reject`);
export const deleteReview = (id) => api.delete(`/api/admin/reviews/${id}`);

// Order tracking API calls (public)
export const trackOrder = (identifier) => api.get(`/api/orders/track/${identifier}`);
export const getCustomerOrders = (phone) => api.get(`/api/orders/customer/${phone}`);

// Carousel API calls (public)
export const getCarouselSlides = () => api.get('/api/public/carousel');

// Carousel Admin API calls
export const getCarouselSlidesAdmin = () => api.get('/api/admin/carousel');
export const createCarouselSlide = (data) => api.post('/api/admin/carousel', data);
export const updateCarouselSlide = (id, data) => api.put(`/api/admin/carousel/${id}`, data);
export const deleteCarouselSlide = (id) => api.delete(`/api/admin/carousel/${id}`);

// 2FA API calls
export const get2FAStatus = () => api.get('/api/auth/2fa/status');
export const setup2FA = () => api.post('/api/auth/2fa/setup');
export const verify2FA = (code) => api.post('/api/auth/2fa/verify', { code });
export const disable2FA = () => api.post('/api/auth/2fa/disable');

// Sessions API calls
export const getSessions = () => api.get('/api/auth/sessions');
export const revokeOtherSessions = () => api.post('/api/auth/sessions/revoke-others');

export default api;
